"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { type PropsWithChildren } from "react"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { GlobalContextProvider } from "@/app/context/GlobalContext"
import { BackgroundJobsProvider } from "@/components/shared/BackgroundJobsProvider"
import { NavSidebar } from "../components/shared/Sidebar"
import { Header } from "../components/shared/Header"

/**
 * Base application layout.
 *
 * Composes the shared NavSidebar + Header shell around the page content
 * and wraps children in GlobalContextProvider and BackgroundJobsProvider.
 *
 * Authentication is handled at the route level via ProtectedRoute in
 * routes/index.tsx — this component assumes the user is already authenticated.
 *
 * @param children - Page content rendered inside the main area.
 */
export function AppLayout({ children }: PropsWithChildren) {
  return (
    <GlobalContextProvider>
      <BackgroundJobsProvider>
        <SidebarProvider>
          {/* Left sidebar with navigation links */}
          <NavSidebar />

          {/* Main content area — grows to fill remaining width */}
          <SidebarInset>
            {/* Top header bar with sidebar toggle, breadcrumb, and logout */}
            <Header />

            {/* Page content */}
            <div className="main-view flex flex-1 flex-col overflow-auto min-w-0 min-h-0">
              {children}
            </div>
          </SidebarInset>
        </SidebarProvider>
      </BackgroundJobsProvider>
    </GlobalContextProvider>
  )
}
